<template>
  <div class="billingContent" v-if="showAllBillingInfo">
    <PaymentInput
      content="First Name"
      :isRequired="true"
      width="half"
      @onChange="setBillingFirstName"
    />
    <PaymentInput
      content="Last Name"
      :isRequired="true"
      width="half"
      @onChange="setBillingLastName"
    />
  </div>
  <div class="billingContent" v-if="showAllBillingInfo">
    <PaymentInput
      :key="`billing-address-${addressFieldsKey}`"
      content="Address Line"
      :isRequired="true"
      width="full"
      @onChange="setBillingAddress"
    />
  </div>
  <div class="billingContent" v-if="showAllBillingInfo">
    <PaymentInput
      :key="`billing-address2-${addressFieldsKey}`"
      content="Address Line 2"
      :isRequired="false"
      width="full"
      @onChange="setBillingAddress2"
    />
  </div>
  <div class="billingContent" v-if="showAllBillingInfo">
    <PaymentInput
      :key="`billing-zip-${addressFieldsKey}`"
      content="Zip Code"
      :isRequired="true"
      width="half"
      @onChange="setBillingZipCode"
    />
    <PaymentInput
      :key="`billing-city-${addressFieldsKey}`"
      content="City"
      :isRequired="true"
      width="half"
      @onChange="setBillingCity"
    />
  </div>
  <div class="billingContent">
    <PaymentSelect
      content="Country"
      :isRequired="true"
      width="half"
      :options="countryList"
      @onChange="handleBillingCountryChange"
      :invalidCountryWarning="invalidCountryWarning"
      :style="{ 'margin-bottom': invalidCountryWarning ? '32px' : '0' }"
    />
    <PaymentSelect
      v-if="this.billing_country == 'US'"
      :key="`billing-state-select-${addressFieldsKey}`"
      content="State"
      :isRequired="true"
      width="half"
      :options="stateList"
      @onChange="setBillingState"
    />
    <!-- VIB-1794: State is only required for a U.S. billing country. For any other country it is a free-text optional
         field, since neither card platform needs it for a non-U.S. address (Stripe is only sent the country). -->
    <PaymentInput
      v-else
      :key="`billing-state-input-${addressFieldsKey}`"
      content="State"
      :isRequired="false"
      width="half"
      @onChange="setBillingState"
    />
  </div>
</template>

<script>
import PaymentInput from "@/components/payment/PaymentInput.vue";
import PaymentSelect from "@/components/payment/PaymentSelect.vue";
import countryList from "@/utils/countryList.js";
import stateList from "@/utils/us_states.js";
import { usePaymentStore } from "@/stores/PaymentStore.ts";
import { mapState, mapActions } from "pinia";
export default {
  name: "BillingInfo",
  components: {
    PaymentInput,
    PaymentSelect,
  },
  props: {
    showAllBillingInfo: {
      type: Boolean,
    },
  },
  data() {
    return {
      countryList,
      stateList,
      invalidCountryWarning: false,
      // VP-18349: bumped when the country changes so the (uncontrolled) address inputs re-mount empty
      addressFieldsKey: 0,
    };
  },
  created() {
    console.log("BillingInfo created");
    this.setBillingFirstName("");
    this.setBillingLastName("");
    this.setBillingAddress("");
    this.setBillingAddress2("");
    this.setBillingZipCode("");
    this.setBillingCity("");
    this.setBillingCountry("");
    this.setBillingState("");
  },
  computed: {
    ...mapState(() => usePaymentStore(), ["billing_country"]),
  },
  watch: {
    billing_country(newVal) {
      if (!newVal || newVal === "") return;
      if (newVal !== "US" && this.showAllBillingInfo) {
        this.invalidCountryWarning = true;
        this.setInvalidCountryWarning(true);
      } else {
        this.invalidCountryWarning = false;
        this.setInvalidCountryWarning(false);
      }
    },
  },
  methods: {
    handleBillingCountryChange(value) {
      // VP-18349: switching to a different country clears address line 1/2, zip, city and state, keeping name and country.
      // Only fires on user selection; first-time selection or re-selecting the same country leaves the fields alone.
      const previous_country = this.billing_country;
      this.setBillingCountry(value);
      if (previous_country && value !== previous_country) {
        this.setBillingAddress("");
        this.setBillingAddress2("");
        this.setBillingZipCode("");
        this.setBillingCity("");
        this.setBillingState("");
        this.addressFieldsKey += 1;
      }
    },
    ...mapActions(
      () => usePaymentStore(),
      [
        "setBillingFirstName",
        "setBillingLastName",
        "setBillingAddress",
        "setBillingAddress2",
        "setBillingZipCode",
        "setBillingCity",
        "setBillingCountry",
        "setBillingState",
        "setInvalidCountryWarning",
      ]
    ),
  },
};
</script>

<style scoped>
.billingContent {
  display: flex;
  justify-content: space-between;
  margin-bottom: 4px;
}

@media only screen and (max-width: 767px) {
  .billingContent {
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
  }
}
</style>
